import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useApi, useAlert } from "@/";
import { useDispatch, useSelector } from "react-redux";
import { updateChannelIO } from "../../../../app/slices";

// Helper function to validate MongoDB ObjectId
const isValidObjectId = (id) => {
  return /^[0-9a-fA-F]{24}$/.test(id);
};

// Helper function to get cookie value by name
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

const ShopifyCallback = () => {
  const { callApi } = useApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Add state to track if we've already attempted to process the callback
  const [processedCallback, setProcessedCallback] = useState(false);

  // Check if user is logged in
  const isLoggedIn = useSelector((state) => Boolean(state?.user?.user?._id));

  useEffect(() => {
    // Clear any stored params if we're on this component without any search params
    // This prevents the component from processing old stored params
    if (!searchParams.toString()) {
      sessionStorage.removeItem("shopifyCallbackParams");
    }

    // Skip processing if we've already tried once or if there are no search params
    if (processedCallback || !searchParams.toString()) {
      return;
    }

    // Mark as processed to prevent infinite loops
    setProcessedCallback(true);

    const params = Object.fromEntries(searchParams.entries());
    const state = params.state;
    const shop = params.shop;

    // First check if we have a state parameter
    if (!state || !shop) {
      console.log("Missing state or shop parameter");
      return;
    }

    // Get userId from cookie
    const userId = getCookie('userId');
    let hasValidUserId = false;

    // Check if userId is a valid MongoDB ObjectId
    if (userId && isValidObjectId(userId)) {
      hasValidUserId = true;
      console.log("Valid MongoDB ObjectId found in cookie");
    }

    // If no valid userId in cookie, redirect to login
    if (!hasValidUserId && !isLoggedIn) {
      console.log("No valid userId in cookie and user not logged in, redirecting");
      // Store the callback URL in sessionStorage to resume after login
      sessionStorage.setItem("shopifyCallbackParams", searchParams.toString());
      // Use window.location instead of navigate to break the React rendering cycle
      window.location.href = "/login";
      return;
    }

    // If we get here, either user is logged in OR we have a valid userId from cookie
    const handleShopifyCallback = async () => {
      console.log("Processing Shopify callback");

      const redirectUri =
        "https://app.indiancouriercompany.com/settings/channel";
      const scopes = ["read_orders", "write_orders", "read_all_orders"].join(
        ","
      );
      const clientId = import.meta.env.VITE_SHOPIFY_CLIENT_ID;

      const installUrl =
        `https://${shop}/admin/oauth/authorize` +
        `?client_id=${clientId}` +
        `&scope=${scopes}` +
        `&redirect_uri=${redirectUri}`;

      try {
        const response = await callApi(
          "/channel-integration/shopify/auth/callback",
          "POST",
          {
            ...params,
            userId,
            refreshTokenUrl: installUrl // Add userId from cookie to the API call
          }
        );

        if (response.success) {
          console.log("Shopify Integration Response:", response);
          if (response.data) dispatch(updateChannelIO(response.data));
          alert("Shopify store connected successfully.");
          setSearchParams({});
        } else {
          console.log("Shopify Integration Failed:", response);
          alert("Failed to connect Shopify store");
        }
      } catch (e) {
        console.error("Shopify integration error:", e);
        alert("Failed to connect Shopify store");
      }
    };

    handleShopifyCallback();
  }, [
    searchParams,
    callApi,
    alert,
    dispatch,
    setSearchParams,
    navigate,
    isLoggedIn,
    processedCallback,
  ]);

  // Modified second useEffect to be more defensive
  useEffect(() => {
    // Only process stored params if we're logged in AND there are no current search params
    if (isLoggedIn && !searchParams.toString()) {
      const storedParams = sessionStorage.getItem("shopifyCallbackParams");
      if (storedParams) {
        // Verify the stored params actually contain shop and state parameters
        const tempParams = new URLSearchParams(storedParams);
        if (tempParams.has("shop") && tempParams.has("state")) {
          // Clear stored params to prevent loops
          sessionStorage.removeItem("shopifyCallbackParams");
          // Set the params and let the other useEffect handle processing
          setSearchParams(tempParams);
        } else {
          // If stored params don't have required fields, just clear them
          sessionStorage.removeItem("shopifyCallbackParams");
        }
      }
    }
  }, [isLoggedIn, searchParams, setSearchParams]);

  return null; // This component doesn't render anything
};

export default ShopifyCallback;
